import clsx from "clsx";
import dynamic from "next/dynamic";
import { useState } from "react";

import useSale from "~/hooks/useSale";
import type { MembershipModalFirstScreenVariant } from "~/lib/membership-modal-experiment";
import saleAnimation from "~/public/lottie-animations/sale.json";
import { FEATURES } from "~/utils/constants";

interface BecomeAMemberFeaturesProps {
	className?: string;
	/** Experiment variant; defaults to control so SignUp / page stay unchanged. */
	variant?: MembershipModalFirstScreenVariant;
	/** Compact padding + collapsed list for denser mobile signup. */
	density?: "default" | "compact";
}

const Lottie = dynamic(
	() => import("lottie-react").then((mod) => mod.default),
	{ ssr: false },
);

const PREVIEW_FEATURE_COUNT = 3;

const BecomeAMemberFeatures = ({
	className,
	variant = "control",
	density = "default",
}: BecomeAMemberFeaturesProps) => {
	const { sale } = useSale();
	const [isExpanded, setIsExpanded] = useState(false);
	const isTest = variant === "test";
	const isCompact = density === "compact";
	// Compact density keeps the full list visible — the 3+ expand
	// experiment already failed; only the first-screen `test` variant collapses.
	const collapseFeatures = isTest;

	const visibleFeatures =
		collapseFeatures && !isExpanded
			? FEATURES.slice(0, PREVIEW_FEATURE_COUNT)
			: FEATURES;

	return (
		<div
			className={clsx(
				"animated-background-slow relative flex h-full flex-col bg-linear-to-br from-yellow-950 via-sky-950 to-yellow-950",
				isCompact
					? "px-3 pt-2 pb-3"
					: "phablet:px-8 px-4 phablet:pt-5 pt-3 pb-5 phablet:pb-6",
				sale ? "items-center justify-center" : "items-start justify-start",
				className,
			)}
		>
			<div
				className={clsx(
					"flex h-full w-full max-w-md flex-col justify-start",
					isCompact ? "gap-1.5" : "gap-2 tablet:gap-4",
				)}
			>
				{sale && (
					<Lottie
						animationData={saleAnimation}
						loop
						className={clsx(
							"dark:brightness-90",
							isCompact
								? "w-28"
								: "tablet:-mt-16 tablet:-mb-10 tablet:w-40 w-36",
						)}
					/>
				)}

				{sale && (
					<p
						className={clsx(
							"w-fit rounded-lg bg-red-500 font-medium text-white",
							isCompact ? "px-2 py-0.5 text-xs" : "px-2.5 py-1 text-sm",
						)}
					>
						Extended, but only until 2pm
					</p>
				)}

				{isTest || isCompact ? (
					<div
						className={clsx("flex flex-col", isCompact ? "gap-1" : "gap-1.5")}
					>
						<h2
							className={clsx(
								"font-bold text-white",
								isCompact ? "text-lg" : "phablet:text-2xl text-xl",
							)}
						>
							One plan unlocks everything
						</h2>
						<p
							className={clsx(
								"text-white/90",
								isCompact ? "text-xs" : "phablet:text-base text-sm",
							)}
						>
							Certifications, courses, videos, and AI coaching — no bundles or
							add-ons.
						</p>
					</div>
				) : (
					<h2 className="font-bold phablet:text-2xl text-white text-xl">
						Included in Membership:
					</h2>
				)}

				<ul
					className={clsx(
						"flex list-inside list-disc flex-col",
						isCompact ? "gap-2.5" : "gap-4 phablet:gap-5",
					)}
				>
					{visibleFeatures.map((feature) => (
						<li
							key={feature.title}
							className={clsx(
								"flex items-center",
								isCompact ? "gap-2.5" : "gap-3",
							)}
						>
							<div
								className={clsx(
									"rounded-xl shadow",
									isCompact ? "p-2" : "p-2.5",
									feature.className,
								)}
							>
								<feature.icon
									className={clsx(isCompact ? "size-4" : "size-5")}
								/>
							</div>

							<div className="flex flex-col gap-0.5">
								<p
									className={clsx(
										"font-medium text-white",
										isCompact && "text-sm",
									)}
								>
									{feature.title}
								</p>
								<p
									className={clsx(
										"text-white",
										isCompact ? "text-xs" : "text-sm",
									)}
								>
									{feature.description}
								</p>
							</div>
						</li>
					))}
				</ul>

				{collapseFeatures &&
					!isExpanded &&
					FEATURES.length > PREVIEW_FEATURE_COUNT && (
						<button
							type="button"
							onClick={() => setIsExpanded(true)}
							className="w-fit font-medium text-sm text-white/90 underline-offset-2 hover:underline"
						>
							See everything included
						</button>
					)}
			</div>
		</div>
	);
};

export default BecomeAMemberFeatures;
